import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Spin } from 'antd';
import { EyeOutlined, RedoOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { orderAPI } from '../services/api';
import { Order } from '../types/order';
import { showErrorToast, showToast } from '../utils/toast';
import type { ColumnsType } from 'antd/es/table';
import { periodLabels } from '../types';

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await orderAPI.getOrders();
      setOrders(response.data.data || []);
    } catch (error: any) {
      showErrorToast(error.response?.data?.message || '获取订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: number) => {
    switch (status) {
      case 0:
        return <Tag color="orange">待支付</Tag>;
      case 1:
        return <Tag color="blue">开通中</Tag>;
      case 2:
        return <Tag color="red">已取消</Tag>;
      case 3:
        return <Tag color="green">已完成</Tag>;
      default:
        return <Tag color="default">未知</Tag>;
    }
  };

  const getOrderType = (type: number) => {
    switch (type) {
      case 1:
        return '新购';
      case 2:
        return '续费';
      case 3:
        return '升级';
      default:
        return '未知';
    }
  };

  const handleViewDetail = (order: Order) => {
    // 直接使用传入的 order 参数进行跳转,避免使用异步更新的 selectedOrder
    handlePayAgain(order);
  };

  const handlePayAgain = (order: Order) => {
    // 跳转到支付页面或重新创建订单
    navigate(`/order/status/${order.trade_no}`);
  };

  const handleCancelOrder = async (order: Order) => {
    try {
      await orderAPI.cancelOrder(order.trade_no);
      showToast('订单已取消');
      fetchOrders(); // 刷新订单列表
    } catch (error: any) {
      showErrorToast(error.response?.data?.message || '取消订单失败');
    }
  };

  const columns: ColumnsType<Order> = [
    {
      title: '订单号',
      dataIndex: 'trade_no',
      key: 'trade_no',
      render: (text: string) => (
        <span className="font-mono text-sm">{text}</span>
      ),
    },
    // {
    //   title: '类型',
    //   dataIndex: 'type',
    //   key: 'type',
    //   render: (type: number) => getOrderType(type),
    // },
    {
      title: '周期',
      dataIndex: 'period',
      key: 'period',
      render: (period: string) => {
        // const periodMap: Record<string, string> = {
        //   'month_price': '月付',
        //   'quarter': '季付',
        //   'half_year': '半年付',
        //   'year': '年付',
        //   'onetime': '一次性',
        //   'reset': '重置包',
        // };
        return periodLabels[period as keyof typeof periodLabels] || period;
      },
    },
    {
      title: '金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount: number) => (
        <span className="font-semibold">¥{(amount / 100).toFixed(2)}</span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => getStatusTag(status),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (timestamp: number) => (
        new Date(timestamp * 1000).toLocaleString()
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record: Order) => (
        <Space size="small">
          <Button
            key="detail"
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {record.status === 0 && (
            <Button
              key="cancel"
              type="link"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => handleCancelOrder(record)}
              danger
            >
              取消
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors">订单记录</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 transition-colors">
          查看您的所有订单记录和支付状态。
        </p>
      </div>

      {/* 桌面端表格视图 */}
      <div className="hidden md:block">
        <Card>
          <Table
            columns={columns}
            dataSource={orders}
            rowKey={(record) => record.trade_no}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            }}
            scroll={{ x: 800 }}
          />
        </Card>
      </div>

      {/* 移动端卡片视图 */}
      <div className="block md:hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spin size="large" />
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => (
              <Card key={order.trade_no || `mobile-order-${index}`} className="shadow-sm">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 mb-1">订单号</div>
                      <div className="font-mono text-sm text-gray-900 break-all">
                        {order.trade_no}
                      </div>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      {getStatusTag(order.status)}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">周期</div>
                      <div className="text-gray-900">
                        {periodLabels[order.period as keyof typeof periodLabels] || order.period}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">金额</div>
                      <div className="font-semibold text-gray-900">
                        ¥{(order.total_amount / 100).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-sm">
                    <div className="text-xs text-gray-500 mb-1">创建时间</div>
                    <div className="text-gray-900">
                      {new Date(order.created_at * 1000).toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex gap-2">
                      <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(order)}
                        className="flex-1"
                      >
                        查看详情
                      </Button>
                      {order.status === 0 && (
                        <Button
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => handleCancelOrder(order)}
                          danger
                          className="flex-1"
                        >
                          取消
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
            
            {orders.length === 0 && (
              <Card>
                <div className="text-center py-8 text-gray-500">
                  暂无订单记录
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* 订单详情模态框 */}
      <Modal
        title="订单详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)} className="flex-1 md:flex-none">
            关闭
          </Button>,
          selectedOrder?.status === 0 ? (
            <Button
              key="pay"
              type="primary"
              onClick={() => {
                setDetailModalVisible(false);
                handlePayAgain(selectedOrder);
              }}
              className="flex-1 md:flex-none"
            >
              去支付
            </Button>
          ) : null,
        ].filter(Boolean)}
        width={600}
        className="mobile-modal"
      >
        {selectedOrder && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <span className="text-gray-600 text-sm">订单号:</span>
                <div className="font-mono text-sm mt-1 break-all">{selectedOrder.trade_no}</div>
              </div>
              <div>
                <span className="text-gray-600 text-sm">状态:</span>
                <div className="mt-1">{getStatusTag(selectedOrder.status)}</div>
              </div>
              <div>
                <span className="text-gray-600 text-sm">周期:</span>
                <div className="mt-1 text-sm">{periodLabels[selectedOrder.period as keyof typeof periodLabels] || selectedOrder.period}</div>
              </div>
              <div>
                <span className="text-gray-600 text-sm">总金额:</span>
                <div className="mt-1 font-semibold text-lg">¥{(selectedOrder.total_amount / 100).toFixed(2)}</div>
              </div>
              {selectedOrder.discount_amount && selectedOrder.discount_amount > 0 && (
                <div>
                  <span className="text-gray-600 text-sm">优惠金额:</span>
                  <div className="mt-1 text-green-600 font-medium">-¥{(selectedOrder.discount_amount / 100).toFixed(2)}</div>
                </div>
              )}
              <div>
                <span className="text-gray-600 text-sm">创建时间:</span>
                <div className="mt-1 text-sm">{new Date(selectedOrder.created_at * 1000).toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-600 text-sm">更新时间:</span>
                <div className="mt-1 text-sm">{new Date(selectedOrder.updated_at * 1000).toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Orders;